import {
  Component,
  ChangeDetectionStrategy,
  computed,
  effect,
  input,
  output,
  signal
} from '@angular/core';
import { WorkflowNodeActionResponseType } from '@formrails/workflow-designer/data-access';

interface ResponseRow {
  key: string;
  path: string;
}

/**
 * Structured editor for Webhook ResponseTemplate. Server-side this is a JSON
 * blob whose values are `{{Response.<Property>}}` (Object type) or
 * `{{Response}}` (String type) — the executor deserialises it into a
 * Dictionary<string,string> and merges it into audience metadata. The
 * builder renders one key/path row per entry and serialises back to JSON;
 * advanced users can drop to raw mode for hand-edited templates.
 */
@Component({
  selector: 'app-response-template-builder',
  standalone: true,
  imports: [],
  templateUrl: './response-template-builder.component.html',
  styleUrl: './response-template-builder.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResponseTemplateBuilderComponent {
  readonly value = input<string>('');
  readonly responseType = input<WorkflowNodeActionResponseType | undefined>(
    WorkflowNodeActionResponseType.None
  );

  readonly valueChange = output<string>();

  readonly raw = signal(false);
  readonly rows = signal<ResponseRow[]>([]);

  /** Static placeholder for the raw JSON textarea. */
  readonly rawPlaceholder = '{ "VentureId": "{{Response.id}}" }';

  /** The path placeholder template (string-typed responses are just `{{Response}}`). */
  readonly pathPlaceholder = computed(() =>
    this.responseType() === WorkflowNodeActionResponseType.String
      ? 'Response'
      : 'Response.PropertyName'
  );

  constructor() {
    // Reset internal rows whenever the upstream value changes.
    effect(() => {
      this.rows.set(parseTemplate(this.value()));
    });
  }

  isStringResponse(): boolean {
    return this.responseType() === WorkflowNodeActionResponseType.String;
  }

  setKey(index: number, key: string): void {
    const next = [...this.rows()];
    next[index] = { ...next[index], key };
    this.emit(next);
  }

  setPath(index: number, path: string): void {
    const next = [...this.rows()];
    next[index] = { ...next[index], path };
    this.emit(next);
  }

  addRow(): void {
    this.emit([
      ...this.rows(),
      { key: '', path: this.isStringResponse() ? 'Response' : '' }
    ]);
  }

  removeRow(index: number): void {
    this.emit(this.rows().filter((_, i) => i !== index));
  }

  onRawChange(value: string): void {
    this.valueChange.emit(value);
  }

  toggleRaw(): void {
    this.raw.update(v => !v);
  }

  private emit(rows: ResponseRow[]): void {
    this.rows.set(rows);
    this.valueChange.emit(serialiseTemplate(rows));
  }
}

function parseTemplate(value: string): ResponseRow[] {
  if (!value?.trim()) return [];
  try {
    const obj = JSON.parse(value);
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
    return Object.entries(obj).map(([key, raw]) => ({
      key,
      path: stripPlaceholder(String(raw))
    }));
  } catch {
    return [];
  }
}

function serialiseTemplate(rows: ResponseRow[]): string {
  const populated = rows.filter(r => r.key.trim() && r.path.trim());
  if (populated.length === 0) return '';
  const obj: Record<string, string> = {};
  for (const r of populated) {
    obj[r.key.trim()] = `{{${r.path.trim()}}}`;
  }
  return JSON.stringify(obj, null, 2);
}

function stripPlaceholder(value: string): string {
  const match = /^\{\{\s*(.+?)\s*\}\}$/.exec(value);
  return match ? match[1] : value;
}
